<template>
  <div
    class="col-span-4 glass-card p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
  >
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Sites Status Overview</h3>
      <GlassSelect
        v-model="currentView"
        @change="saveViewPreference"
        :options="[
          { value: 'doughnut', label: 'Doughnut Chart' },
          { value: 'bar', label: 'Bar Chart' },
          { value: 'numbers', label: 'Numbers View' },
        ]"
        :valueKey="'value'"
        :labelKey="'label'"
        placeholder="Select View"
        class="w-auto"
      />
    </div>

    <!-- Numbers View -->
    <div v-if="currentView === 'numbers'" class="space-y-4">
      <div
        v-for="(item, index) in chartData"
        :key="index"
        class="flex items-center justify-between"
      >
        <span class="text-gray-600 dark:text-white">{{ item.name }}</span>
        <span class="text-gray-900 dark:text-white font-medium">{{ item.value }}</span>
      </div>
      <div class="pt-2 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <span class="text-gray-600 dark:text-white font-semibold">Total Sites</span>
          <span class="text-gray-900 dark:text-white font-semibold">{{ siteData.total }}</span>
        </div>
      </div>
    </div>

    <!-- Doughnut Chart View -->
    <div v-else-if="currentView === 'doughnut'" class="h-80">
      <Doughnut :data="doughnutData" :options="doughnutOptions" />
    </div>

    <!-- Bar Chart View -->
    <div v-else class="h-80">
      <Bar :data="barData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Cookies from "js-cookie";
import GlassSelect from "@/components/GlassSelect.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const props = defineProps({
  siteData: {
    type: Object,
    required: true,
    default: () => ({
      total: 0,
      by_status: {
        in_maintenance: 0,
        pending: 0,
      },
    }),
  },
});

const currentView = ref("doughnut");
const COOKIE_NAME = "preferred-sites-status-view";
const COOKIE_EXPIRES = 30;

onMounted(() => {
  const savedView = Cookies.get(COOKIE_NAME);
  if (savedView && ["doughnut", "bar", "numbers"].includes(savedView)) {
    currentView.value = savedView;
  }
});

const saveViewPreference = () => {
  Cookies.set(COOKIE_NAME, currentView.value, { expires: COOKIE_EXPIRES });
};

// Colors for different statuses - using semantic colors
const colors = {
  active: "#22C55E", // success color
  maintenance: "#1E40AF", // primary color  
  pending: "#EAB308", // warning color
};

// Transform data for charts
const chartData = computed(() => {
  const activeCount =
    props.siteData.total -
    (props.siteData.by_status.in_maintenance || 0) -
    (props.siteData.by_status.pending || 0);

  return [
    {
      name: "Active Sites",
      value: activeCount,
      color: colors.active,
    },
    {
      name: "In Maintenance",
      value: props.siteData.by_status.in_maintenance || 0,
      color: colors.maintenance,
    },
    {
      name: "Pending Changes",
      value: props.siteData.by_status.pending || 0,
      color: colors.pending,
    },
  ];
});

// Doughnut Chart Data and Options
const doughnutData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      data: chartData.value.map((item) => item.value),
      backgroundColor: chartData.value.map((item) => item.color),
      borderColor: "white",
      borderWidth: 2,
    },
  ],
}));

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12 },
      },
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const total = props.siteData.total;
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value} sites (${percentage}%)`;
        },
      },
    },
    datalabels: {
      color: "rgb(17 24 39)",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      font: {
        weight: "bold",
        size: 13,
      },
      formatter: (value) => {
        const percentage = ((value / props.siteData.total) * 100).toFixed(1);
        return value > 0 ? `${percentage}%` : "";
      },
      textAlign: "center",
    },
  },
};

// Bar Chart Data and Options
const barData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      data: chartData.value.map((item) => item.value),
      backgroundColor: chartData.value.map((item) => item.color),
      borderColor: chartData.value.map((item) => item.color),
      borderWidth: 1,
      barPercentage: 0.6,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const total = props.siteData.total;
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value} sites (${percentage}%)`;
        },
      },
    },
    datalabels: {
      anchor: "end",
      align: "top",
      offset: 4,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      color: "rgb(17 24 39)",
      font: {
        weight: "bold",
        size: 12,
      },
      formatter: (value) => value.toLocaleString(),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
      grid: {
        display: true,
      },
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}));
</script>
